import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Compass, Star, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

const VERDICT_STYLE = {
  emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
  green: "bg-green-50 border-green-300 text-green-800",
  amber: "bg-amber-50 border-amber-300 text-amber-800",
  red: "bg-red-50 border-red-300 text-red-800",
};

const DIRECTION_ICONS = {
  N: "↑", NE: "↗", E: "→", SE: "↘", S: "↓", SW: "↙", W: "←", NW: "↖",
};

/**
 * VastuPanel — displays Vastu insights for a property.
 * Props:
 *   category (required): "ghar" | "plot" | "shop" | "jamin"
 *   direction (optional): "N" | "NE" | ...  (if not provided, shows full grid preview)
 *   compact (bool): shorter card mode for detail page
 */
export default function VastuPanel({ category, direction, compact = false }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    const params = direction ? { category, direction } : { category };
    api.get("/vastu/preview", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [category, direction]);

  if (loading) return (
    <div className="rounded-xl border border-dashed border-border p-4 text-xs text-muted-foreground animate-pulse">
      {lang === "hi" ? "वास्तु विश्लेषण लोड हो रहा है…" : "Loading Vastu analysis…"}
    </div>
  );

  if (!data) return null;

  // Single direction mode
  if (direction && data.rating != null) {
    const style = VERDICT_STYLE[data.verdict.color] || VERDICT_STYLE.amber;
    return (
      <div className={`rounded-xl border-2 p-4 ${style}`} data-testid="vastu-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Compass size={16} />
            <span className="text-[10px] uppercase tracking-widest font-semibold opacity-80">
              {lang === "hi" ? "वास्तु विश्लेषण" : "Vastu Analysis"}
            </span>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold flex items-center gap-1">
              {data.rating}<span className="text-sm opacity-60">/10</span>
              <Star size={16} fill="currentColor" className="ml-1" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-14 h-14 rounded-full grid place-items-center border-2 border-current text-2xl font-bold bg-white/60">
            {DIRECTION_ICONS[direction]}
          </div>
          <div>
            <div className="font-bold text-base leading-tight">
              {lang === "hi" ? data.direction_name?.hi : data.direction_name?.en}
            </div>
            <div className="text-xs opacity-80">
              {lang === "hi" ? "देवता:" : "Deity:"} <span className="font-semibold">{data.deity}</span>
            </div>
            <div className="text-xs font-semibold mt-0.5">
              {lang === "hi" ? data.verdict.hi : data.verdict.en}
            </div>
          </div>
        </div>

        {data.positives?.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mb-1 flex items-center gap-1">
              <CheckCircle2 size={11} />{lang === "hi" ? "शुभ बातें" : "Positives"}
            </div>
            <ul className="space-y-1 text-xs">
              {data.positives.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-emerald-700 mt-[3px]">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.cautions?.length > 0 && !compact && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mb-1 flex items-center gap-1">
              <AlertTriangle size={11} />{lang === "hi" ? "सावधानी" : "Cautions"}
            </div>
            <ul className="space-y-1 text-xs">
              {data.cautions.map((c, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-amber-700 mt-[3px]">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Grid preview mode (no direction selected)
  if (data.directions) {
    return (
      <div className="rounded-xl border border-border p-4 bg-accent/40" data-testid="vastu-grid">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-secondary" />
          <span className="text-[10px] uppercase tracking-widest font-semibold text-secondary">
            {lang === "hi" ? "सभी दिशाओं का वास्तु स्कोर" : "Vastu Score by Direction"}
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {data.directions.map((d) => (
            <div key={d.direction} className={`rounded-md p-2 text-center border ${VERDICT_STYLE[d.verdict.color] || VERDICT_STYLE.amber}`}>
              <div className="text-lg font-bold">{DIRECTION_ICONS[d.direction]}</div>
              <div className="text-[10px] font-semibold">{lang === "hi" ? d.direction_name.hi.split(" ")[0] : d.direction_name.en}</div>
              <div className="text-sm font-bold mt-0.5">{d.rating}/10</div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 italic">
          {lang === "hi" ? "मुख्य द्वार की दिशा चुनें — विस्तृत विश्लेषण दिखेगा।" : "Pick your main entrance direction — detailed analysis will appear."}
        </p>
      </div>
    );
  }

  return null;
}
